import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { colors as C } from "@/lib/theme";
import { Ion } from "@/components/Ion";
import { Screen } from "@/components/Orig";
import { AdminHeader } from "@/components/AdminParts";

// Port of app/admin/broadcast.tsx
const Card = ({ children, className = "" }) => <div className={`mb-3 rounded-[14px] border p-4 ${className}`} style={{ background: C.card, borderColor: C.border }}>{children}</div>;
const Kicker = ({ children, className = "" }) => <p className={`mb-2.5 text-[10px] font-extrabold tracking-[2px] ${className}`} style={{ color: C.accent }}>{children}</p>;
const fieldCls = "w-full rounded-[10px] border border-[#333] bg-[#121212] px-3 py-2.5 text-sm text-white placeholder:text-[#A0A0A0] focus:border-[var(--accent)] focus:outline-none";

export default function Broadcast() {
  const [audiences, setAudiences] = useState([]);
  const [history, setHistory] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("franchisees");
  const [previewHtml, setPreviewHtml] = useState(null);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const loadHistory = useCallback(() => api("/admin/broadcast/history?limit=20").then((h) => setHistory(h || [])).catch(() => {}), []);
  useEffect(() => { api("/admin/broadcast/audiences").then((a) => setAudiences(a || [])).catch(() => {}); loadHistory(); }, [loadHistory]);
  const selected = useMemo(() => audiences.find((a) => a.key === audience), [audiences, audience]);
  const draft = () => ({ subject: subject.trim(), body_text: body, audience });

  const doPreview = async () => {
    if (!subject.trim() || !body.trim()) return toast.error("Add a subject and body — both fields are required to preview.");
    setPreviewing(true);
    try { setPreviewHtml((await api("/admin/broadcast/preview", { method: "POST", body: draft() })).html); }
    catch (e) { toast.error(`Preview failed: ${e.message || "Try again."}`); } finally { setPreviewing(false); }
  };

  const doSend = async () => {
    if (!subject.trim() || !body.trim()) return toast.error("Add a subject and body — both fields are required to send.");
    const count = selected?.count ?? 0;
    if (!window.confirm(`Send to ${count} recipient${count === 1 ? "" : "s"}?\n\nAudience: ${selected?.label || audience}\nSubject: ${subject}\n\nThis cannot be undone.`)) return;
    setSending(true);
    try {
      const r = await api("/admin/broadcast/send", { method: "POST", body: draft() });
      toast.success(`Broadcast sent — delivered ${r.delivered_count} of ${r.recipient_count_total}.${r.failed_count > 0 ? ` Failed: ${r.failed_count}.` : ""}`);
      setSubject(""); setBody(""); setPreviewHtml(null); await loadHistory();
    } catch (e) { toast.error(`Send failed: ${e.message || "Try again."}`); } finally { setSending(false); }
  };

  if (previewHtml) return (
    <Screen testId="broadcast-preview-page" className="flex flex-col">
      <div className="flex items-center gap-4 border-b p-4" style={{ borderColor: C.border }}>
        <button onClick={() => setPreviewHtml(null)} className="p-1.5" aria-label="Back to compose" data-testid="broadcast-preview-back"><Ion name="arrow-back" size={22} color={C.text} /></button>
        <div className="min-w-0 flex-1"><p className="text-[10px] font-extrabold tracking-[2.5px]" style={{ color: C.accent }}>PREVIEW</p><p className="truncate text-xl font-extrabold">{subject}</p></div>
        <button onClick={doSend} disabled={sending} data-testid="broadcast-preview-send" className="flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[11px] font-extrabold tracking-[1px] text-black" style={{ background: C.accent }}>
          {sending ? <Loader2 size={14} className="animate-spin" /> : <><Ion name="send" size={13} color="#000" />SEND</>}
        </button>
      </div>
      <iframe srcDoc={previewHtml} title="Email preview" sandbox="allow-same-origin" className="w-full flex-1 border-0" style={{ background: "#0A0A0A", minHeight: "calc(100vh - 80px)" }} data-testid="broadcast-preview-frame" />
    </Screen>
  );

  return (
    <Screen testId="admin-broadcast-page">
      <div className="mx-auto max-w-[860px]">
        <div className="border-b" style={{ borderColor: C.border }}><AdminHeader eyebrow="ADMIN · EMAIL BROADCAST" title="Send to your team" icon="arrow-back" /></div>
        <div className="p-4 pb-16">
          <Card>
            <Kicker>AUDIENCE</Kicker>
            <div className="flex flex-wrap gap-2">
              {audiences.map((a) => {
                const on = a.key === audience;
                return (
                  <button key={a.key} onClick={() => setAudience(a.key)} data-testid={`broadcast-audience-${a.key}`} className="flex items-center gap-1.5 rounded-full border px-3 py-2 text-xs transition-colors"
                    style={{ borderColor: on ? C.accent : C.border, background: on ? "rgba(76,204,6,0.13)" : C.surface, color: on ? C.accent : C.text, fontWeight: on ? 800 : 600 }}>
                    {a.label.split("(")[0].trim()}
                    <span className="min-w-[22px] rounded-full border px-1.5 py-px text-center text-[10px] font-extrabold" style={on ? { background: C.accent, borderColor: C.accent, color: "#000" } : { background: C.surface, borderColor: C.border, color: C.text }}>{a.count}</span>
                  </button>
                );
              })}
            </div>
            {selected && <p className="mt-2 text-[11px] leading-4" style={{ color: C.textSecondary }} data-testid="broadcast-audience-summary">Sending to <b style={{ color: C.accent }}>{selected.count}</b> active user{selected.count === 1 ? "" : "s"} · {selected.label.toLowerCase()}</p>}
          </Card>
          <Card>
            <Kicker>SUBJECT</Kicker>
            <input data-testid="broadcast-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Q3 LSO renewal reminder" maxLength={200} className={fieldCls} />
            <Kicker className="mt-4">BODY</Kicker>
            <textarea data-testid="broadcast-body" value={body} onChange={(e) => setBody(e.target.value)} maxLength={20000} rows={10} className={`${fieldCls} min-h-[180px] resize-y`}
              placeholder={"Type your message.\n\n**Bold** with double-asterisks.\n*Italic* with single asterisks.\n- Bullets with a dash"} />
            <p className="mt-2 text-[11px] leading-4" style={{ color: C.textSecondary }}>Markdown-lite: **bold**, *italic*, "- " bullets, blank line for new paragraph. The greeting + signoff + branded header & footer (logo, phone, email, address) are added automatically.</p>
          </Card>
          <div className="flex gap-2.5">
            <button onClick={doPreview} disabled={previewing || sending} data-testid="broadcast-preview-btn" className="flex items-center justify-center gap-1.5 rounded-lg border px-4 py-3.5 text-xs font-extrabold tracking-[1px] disabled:opacity-50" style={{ borderColor: C.border, background: C.surface }}>
              {previewing ? <Loader2 size={14} className="animate-spin" /> : <><Ion name="eye-outline" size={14} color={C.text} />PREVIEW</>}
            </button>
            <button onClick={doSend} disabled={sending || previewing} data-testid="broadcast-send-btn" className="flex flex-1 items-center justify-center gap-1.5 rounded-lg py-3.5 text-xs font-extrabold tracking-[1px] text-black disabled:opacity-50" style={{ background: C.accent }}>
              {sending ? <Loader2 size={14} className="animate-spin" /> : <><Ion name="send" size={14} color="#000" />SEND BROADCAST</>}
            </button>
          </div>
          {history.length > 0 && (
            <Card className="mt-[18px]">
              <Kicker>RECENT BROADCASTS</Kicker>
              {history.map((h) => (
                <div key={h.id} className="border-t py-2.5" style={{ borderColor: C.border }} data-testid={`broadcast-history-${h.id}`}>
                  <p className="truncate text-[13px] font-bold">{h.subject}</p>
                  <p className="mt-0.5 text-[11px]" style={{ color: C.textSecondary }}>{new Date(h.sent_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}  ·  {h.delivered_count}/{h.recipient_count_total} delivered{h.failed_count > 0 ? `  ·  ${h.failed_count} failed` : ""}  ·  {h.audience}</p>
                </div>
              ))}
            </Card>
          )}
        </div>
      </div>
    </Screen>
  );
}
